import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, MapPin, Users, Instagram, Youtube, Twitter,
  Loader2, MessageSquare, DollarSign, Globe, ExternalLink, CheckCircle2
} from 'lucide-react';

// Verified Badge Component
const VerifiedBadge = ({ size = 'md', showLabel = true }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="inline-flex items-center gap-1.5" title="Verified Creator">
      <div className={`${sizeClasses[size]} rounded-full bg-[#3AAFB9] flex items-center justify-center`}>
        <CheckCircle2 className={`${size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-white`} />
      </div>
      {showLabel && <span className="text-sm font-medium text-[#3AAFB9]">Verified</span>}
    </div>
  );
};

const CreatorProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);

  const isBrand = user?.role === 'brand';
  const isOwnProfile = user?.user_id === userId;

  const fetchCreator = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/users/${userId}`);
      setCreator(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchCreator();
  }, [fetchCreator]);

  const startMessage = async () => {
    try {
      const response = await api.post('/messages/conversations', {
        participant_id: userId,
        initial_message: `Hi! I'd like to discuss a potential collaboration.`
      });
      navigate(user?.role === 'creator' ? '/creator/messages' : '/brand/messages');
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!creator) return null;

  const profile = creator.creator_profile || {};
  const isCreatorRole = creator.role === 'creator';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Profile Header */}
        <div className="relative mb-8">
          {/* Cover/Banner */}
          <div className="h-32 rounded-t-2xl bg-gradient-to-r from-[#E8795A]/30 via-primary/20 to-[#3AAFB9]/30"></div>
          
          {/* Avatar & Basic Info */}
          <div className="relative px-6 pb-6 -mt-12">
            <div className="flex flex-col md:flex-row md:items-end gap-6">
              <div className="w-24 h-24 rounded-2xl bg-card border-4 border-background overflow-hidden flex-shrink-0">
                {creator.picture ? (
                  <img src={creator.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary">
                    <Users className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                        {creator.name}
                      </h1>
                      {profile.verified && <VerifiedBadge size="md" showLabel={true} />}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {isCreatorRole && profile.follower_range && (
                        <Badge className="bg-[#E8795A] text-background">
                          {profile.follower_range} followers
                        </Badge>
                      )}
                      {profile.location && (
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          {profile.location}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {isBrand && !isOwnProfile && (
                    <Button onClick={startMessage} data-testid="message-btn">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Bio - Full Width */}
          {profile.bio && (
            <Card className="md:col-span-3">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-3">About</h2>
                <p className="text-muted-foreground whitespace-pre-wrap">{profile.bio}</p>
              </CardContent>
            </Card>
          )}

          {/* Niches */}
          {isCreatorRole && profile.niches?.length > 0 && (
            <Card className="md:col-span-2">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-3">Niches</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.niches.map(niche => (
                    <Badge key={niche} variant="secondary" className="text-sm">
                      {niche}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rate */}
          {isCreatorRole && profile.rate_per_post > 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#E8795A]/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-[#84CC16]" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Rate per post</p>
                    <p className="text-2xl font-bold">${profile.rate_per_post}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Platforms */}
          {isCreatorRole && profile.platforms?.length > 0 && (
            <Card className="md:col-span-2">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-3">Platforms</h2>
                <div className="flex flex-wrap gap-3">
                  {profile.platforms.map(platform => (
                    <div 
                      key={platform}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary"
                    >
                      {platform === 'Instagram' && <Instagram className="w-5 h-5 text-pink-500" />}
                      {platform === 'YouTube' && <Youtube className="w-5 h-5 text-red-500" />}
                      {platform === 'Twitter' && <Twitter className="w-5 h-5 text-blue-400" />}
                      {platform === 'TikTok' && <span className="text-lg">📱</span>}
                      {platform === 'LinkedIn' && <span className="text-lg">💼</span>}
                      <span>{platform}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Social Handles */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-3">Social Links</h2>
              <div className="space-y-3">
                {profile.instagram_handle && (
                  <a 
                    href={`https://instagram.com/${profile.instagram_handle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Instagram className="w-5 h-5 text-pink-500" />
                    <span>{profile.instagram_handle}</span>
                    <ExternalLink className="w-4 h-4 ml-auto" />
                  </a>
                )}
                {profile.youtube_handle && (
                  <a 
                    href={`https://youtube.com/@${profile.youtube_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Youtube className="w-5 h-5 text-red-500" />
                    <span>{profile.youtube_handle}</span>
                    <ExternalLink className="w-4 h-4 ml-auto" />
                  </a>
                )}
                {profile.tiktok_handle && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <span className="text-lg">📱</span>
                    <span>{profile.tiktok_handle}</span>
                  </div>
                )}
                {profile.portfolio_url && (
                  <a 
                    href={profile.portfolio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Globe className="w-5 h-5" />
                    <span>Portfolio</span>
                    <ExternalLink className="w-4 h-4 ml-auto" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA for Brands */}
        {isBrand && !isOwnProfile && (
          <div className="mt-8 text-center">
            <Button 
              size="lg"
              onClick={startMessage}
              className="rounded-full px-8 btn-glow"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Start a Conversation
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorProfile;
